import React, { useState, useEffect, useCallback } from "react";
import styles from "./PushPermissionModal.module.css";
import AnimatedButton from "../AnimatedButton/AnimatedButton";
import {
  isNativeApp,
  requestNativePermission,
} from "../../utils/nativePermissions";

// Once the user clicks "Not now", we remember it for the rest of the
// session. The flag is cleared by the logout handler's localStorage.clear()
// — so the popup only re-appears after the user logs out and signs back in.
const PUSH_DISMISSED_KEY = "rexpt_push_dismissed";

/**
 * PushPermissionModal
 * 
 * Custom modal that asks user for push notification permission 
 * BEFORE triggering the browser's native popup.
 * 
 * Props:
 *  - onTokenReceived(token)  → called when FCM token is successfully obtained
 *  - forceOpen               → true to force-show (e.g. from Settings page re-enable)
 */
const PushPermissionModal = ({ onTokenReceived, forceOpen = false, disabled = false, onOpen, onClose: onCloseProp }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);

  const silentlyFetchToken = useCallback(async () => {
    try {
      const { fetchToken } = await import("../../firebase");
      const token = await fetchToken();
      if (token && onTokenReceived) {
        onTokenReceived(token);
      }
    } catch (e) {
      console.error("[PushModal] Silent token fetch failed:", e);
    }
  }, [onTokenReceived]);

  useEffect(() => {
    if (disabled) {
      if ((isOpen || isBlocked) && onCloseProp) onCloseProp();
      setIsOpen(false);
      setIsBlocked(false);
    }
    // Intentionally run only when the queue disables this modal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled]);

  useEffect(() => {
    if (disabled) return;

    // Native shell: the OS notification permission is handled via the bridge,
    // NOT the web Notification API (which doesn't reflect Android's OS state).
    // Never auto-pop on app load — only show when the parent explicitly asks
    // (forceOpen), e.g. after the first real call completes or a Settings
    // re-enable. handleAllow() routes the actual prompt through the bridge.
    if (isNativeApp()) {
      if (forceOpen) {
        setIsOpen(true);
      }
      return;
    }

    // Check if notifications API is available
    if (!("Notification" in window)) {
      if (forceOpen) {
        // Fallback for WebView/environment without window.Notification:
        // Signal to parent and close cleanly
        const storedToken = localStorage.getItem("fcmToken");
        if (onTokenReceived) onTokenReceived(storedToken || "granted");
        if (onCloseProp) onCloseProp();
      }
      return;
    }

    const permission = Notification.permission;

    // Already granted → silently fetch token
    if (permission === "granted") {
      silentlyFetchToken();
      return;
    }

    // Already blocked by browser → don't show modal unless forceOpen
    if (permission === "denied") {
      if (forceOpen) {
        setIsBlocked(true);
        setIsOpen(true);
      }
      return;
    }

    // forceOpen se modal hamesha dikhega
    if (forceOpen) {
      setIsOpen(true);
      return;
    }

    // User already dismissed in this login session — don't ask again
    // until they log out and log back in (logout clears the flag).
    if (localStorage.getItem(PUSH_DISMISSED_KEY)) return;

    // Show custom modal after 2s delay (let page load first)
    const timer = setTimeout(() => setIsOpen(true), 2000);
    return () => clearTimeout(timer);
  }, [forceOpen, disabled, onCloseProp, onTokenReceived, silentlyFetchToken]);

  useEffect(() => {
    if (isOpen && onOpen) onOpen();
  }, [isOpen, onOpen]);

  // User clicked "Enable Notifications"
  const handleAllow = async () => {
    try {
      // Native shell: this modal already IS the soft-ask, so skip native's own
      // soft-ask and fire the OS notification prompt straight via the bridge.
      // The FCM token then arrives through the existing native bridge listener
      // (see initNotificationSocket setupNativeBridge / FCM_TOKEN).
      if (isNativeApp()) {
        const status = await requestNativePermission(
          "NOTIFICATIONS",
          { app: "Rexpt" },
          { skipSoftAsk: true }
        );
        if (status === "granted") {
          localStorage.removeItem(PUSH_DISMISSED_KEY);
          const storedToken = localStorage.getItem("fcmToken");
          if (onTokenReceived) {
            onTokenReceived(storedToken || "granted");
          }
        }
        // "blocked" → native already showed its recovery card.
        setIsOpen(false);
        if (onCloseProp) onCloseProp();
        return;
      }

      const permission = await Notification.requestPermission();

      if (permission === "granted") {
        // Get FCM token
        await silentlyFetchToken();
        setIsOpen(false);
        localStorage.removeItem(PUSH_DISMISSED_KEY);
        if (onCloseProp) onCloseProp();
      } else if (permission === "denied") {
        // Browser blocked — show instructions
        setIsBlocked(true);
        const storedToken = localStorage.getItem("fcmToken");
        const authToken = localStorage.getItem("token");
        if (storedToken && authToken) {
          try {
            const { default: decodeToken } = await import("../../lib/decodeToken");
            const uid = decodeToken(authToken)?.id;
            if (uid) {
              const { updatePushPermissionStatus } = await import("../../utils/initNotificationSocket");
              updatePushPermissionStatus(uid, storedToken, false);
            }
          } catch (e) { }
        }
      }
    } catch (err) {
      console.error("[PushModal] Permission request failed:", err);
    }
  };

  // User clicked "Not Now"
  const handleDismiss = () => {
    // Persists until logout — see PUSH_DISMISSED_KEY note above.
    localStorage.setItem(PUSH_DISMISSED_KEY, "1");
    setIsOpen(false);
    if (onCloseProp) onCloseProp();
  };

  const handleClose = () => {
    setIsOpen(false);
    setIsBlocked(false);
    if (onCloseProp) onCloseProp();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {!isBlocked ? (
          /* ─── Permission Ask View ──────────────────── */
          <div className={styles.body}>
            <div className={styles.iconWrap}>
              <svg className={styles.bellIcon} width="34" height="34" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M6 16v-5a6 6 0 0 1 12 0v5l1.5 2h-15L6 16Z"
                  stroke="#fff"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path d="M10 20a2 2 0 0 0 4 0" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </div>

            <h2 className={styles.title}>Stay Updated in Real-Time</h2>
            <p className={styles.description}>
              Enable push notifications to get instant alerts even when you're not on the app.
            </p>

            <ul className={styles.features}>
              <li>
                <span className={styles.featureIcon} aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M5 4h3l2 5-2.5 1.5a11 11 0 0 0 6 6L15 14l5 2v3a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2Z"
                      stroke="#6524EB"
                      strokeWidth="1.8"
                      strokeLinejoin="round"
                    />
                    <path d="M15 4l5 5M20 4l-5 5" stroke="#6524EB" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                </span>
                Missed call & urgent callback alerts
              </li>
              <li>
                <span className={styles.featureIcon} aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M12 3l7 3v5c0 4.5-3 8.3-7 10-4-1.7-7-5.5-7-10V6l7-3Z"
                      stroke="#6524EB"
                      strokeWidth="1.8"
                      strokeLinejoin="round"
                    />
                    <path d="M9 12l2.2 2.2L15 10" stroke="#6524EB" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                Spam detection & auto-block notifications
              </li>
              {/* <li>
                <span className={styles.featureIcon}>📊</span>
                Usage tracking & minutes running low
              </li> */}
              <li>
                <span className={styles.featureIcon} aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M4 8h14M14 4l4 4-4 4" stroke="#6524EB" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M20 16H6M10 12l-4 4 4 4" stroke="#6524EB" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                Failed call transfer warnings
              </li>
            </ul>

            <div className={styles.btnGroup}>
              {/* Shared AnimatedButton. Wo default me position: fixed hota hai —
                  yahan modal ke andar relative chahiye. bgSrc me leading slash:
                  default path relative hai, nested route pe 404 ho jaata. */}
              <AnimatedButton
                label="Enable Notifications"
                onClick={handleAllow}
                positionType="relative"
                bottom="0px"
                width="100%"
                bgSrc="/svg/svg-theme2.svg"
              />
              <button className={styles.laterBtn} onClick={handleDismiss}>
                Not Now
              </button>
            </div>
          </div>
        ) : (
          /* ─── Blocked / Re-enable Instructions ─────── */
          <div className={styles.blockedWrap}>
            <div className={styles.blockedIcon} aria-hidden="true">
              {/* Ghanti pe tirchhi line — "notifications band". */}
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
                <path
                  d="M6 16V11a6 6 0 0 1 9.3-5M18 11v5l1.5 2H8"
                  stroke="#fff"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path d="M10 20a2 2 0 0 0 4 0" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
                <path d="M4 4l16 16" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </div>

            <h2 className={styles.title}>Notifications Blocked</h2>
            <p className={styles.description}>
              It looks like you've blocked notifications in your browser. Follow these steps to re-enable:
            </p>

            <h3 className={styles.stepsTitle}>How to Re-enable:</h3>
            <ol className={styles.steps}>
              {/* Har step ka text EK hi span (.stepText) me hai. <li> flex hai;
                  bina span ke text ke tukde aur har <strong> alag-alag flex
                  item ban kar columns me toot jaate the. */}
              <li>
                <span className={styles.stepNum}>1</span>
                <span className={styles.stepText}>
                  Click the{" "}
                  <strong>
                    lock icon{" "}
                    <svg className={styles.inlineIcon} width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <rect x="5" y="11" width="14" height="9" rx="2" stroke="currentColor" strokeWidth="2" />
                      <path d="M8 11V8a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </strong>{" "}
                  (or info icon) in your browser's address bar
                </span>
              </li>
              <li>
                <span className={styles.stepNum}>2</span>
                <span className={styles.stepText}>
                  Find <strong>"Notifications"</strong> in the permissions list
                </span>
              </li>
              <li>
                <span className={styles.stepNum}>3</span>
                <span className={styles.stepText}>
                  Change it from <strong>"Block"</strong> to <strong>"Allow"</strong>
                </span>
              </li>
              <li>
                <span className={styles.stepNum}>4</span>
                <span className={styles.stepText}>
                  <strong>Refresh the page</strong> — notifications will activate automatically
                </span>
              </li>
            </ol>

            <div className={styles.btnGroup}>
              <AnimatedButton
                label="Got It"
                onClick={handleClose}
                positionType="relative"
                bottom="0px"
                width="100%"
                bgSrc="/svg/svg-theme2.svg"
              />
            </div>
          </div>
        )}
      </div>

      <button
        className={styles.closeBtn}
        onClick={handleClose}
        aria-label="Close modal"
      >
        ×
      </button>
    </div>
  );
};

export default PushPermissionModal;
